import React, { useState } from 'react';
import { LogoLoading, LoadingScreen, AnimatedLogo } from './index';

export const LoadingExamples: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [currentAnimation, setCurrentAnimation] = useState<'rotate' | 'bounce' | 'pulse' | 'float'>('float');

  const handleSimulateLoading = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 3000);
  };

  const handleShowFullScreen = () => {
    setShowFullScreen(true);
    setTimeout(() => setShowFullScreen(false), 3000);
  };

  if (showFullScreen) {
    return <LoadingScreen message="Carregando dados da aplicação..." />;
  }

  return (
    <div className="p-8 space-y-8">
      <h1 className="text-3xl font-extrabold text-center mb-8">
        Exemplos de Loading com Logo Animado
      </h1>

      {/* Seção 1: LogoLoading Simples */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-extrabold text-gray-800 mb-4">
          1. LogoLoading Simples
        </h2>
        <p className="text-gray-600 mb-4">
          Componente ideal para carregamentos pequenos em botões ou áreas específicas.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <h3 className="font-extrabold text-gray-700 mb-2">Tamanho Pequeno</h3>
            <LogoLoading size={40} />
          </div>
          <div className="text-center">
            <h3 className="font-extrabold text-gray-700 mb-2">Tamanho Médio</h3>
            <LogoLoading size={80} />
          </div>
          <div className="text-center">
            <h3 className="font-extrabold text-gray-700 mb-2">Tamanho Grande</h3>
            <LogoLoading size={120} />
          </div>
        </div>
      </section>

      {/* Seção 2: AnimatedLogo com Diferentes Animações */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-extrabold text-gray-800 mb-4">
          2. AnimatedLogo com Diferentes Animações
        </h2>
        <p className="text-gray-600 mb-4">
          Componente flexível com diferentes tipos de animação.
        </p>
        
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {(['rotate', 'bounce', 'pulse', 'float'] as const).map((animation) => (
              <button
                key={animation}
                onClick={() => setCurrentAnimation(animation)}
                className={`px-4 py-2 rounded-md text-sm font-extrabold transition-colors ${
                  currentAnimation === animation
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {animation.charAt(0).toUpperCase() + animation.slice(1)}
              </button>
            ))}
          </div>
          
          <div className="text-center">
            <AnimatedLogo size={100} animationType={currentAnimation} />
          </div>
        </div>
      </section>

      {/* Seção 3: LoadingScreen Completo */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-extrabold text-gray-800 mb-4">
          3. LoadingScreen Completo
        </h2>
        <p className="text-gray-600 mb-4">
          Tela completa de loading para carregamentos de página ou aplicação.
        </p>
        
        <div className="space-y-4">
          <button
            onClick={handleShowFullScreen}
            className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            Mostrar LoadingScreen Completo (3s)
          </button>
          
          <div className="text-sm text-gray-500">
            Clique no botão acima para ver o LoadingScreen em tela cheia por 3 segundos.
          </div>
        </div>
      </section>

      {/* Seção 4: Exemplo Prático */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-extrabold text-gray-800 mb-4">
          4. Exemplo Prático - Botão com Loading
        </h2>
        <p className="text-gray-600 mb-4">
          Como usar o LogoLoading em um botão real.
        </p>
        
        <div className="space-y-4">
          <button
            onClick={handleSimulateLoading}
            disabled={isLoading}
            className={`px-6 py-3 rounded-md font-extrabold transition-colors ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <LogoLoading size={20} />
                <span>Processando...</span>
              </div>
            ) : (
              'Simular Processamento'
            )}
          </button>
          
          <div className="text-sm text-gray-500">
            Clique no botão para simular um processo de 3 segundos com loading.
          </div>
        </div>
      </section>

      {/* Seção 5: Código de Exemplo */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-extrabold text-gray-800 mb-4">
          5. Como Implementar
        </h2>
        
        <div className="bg-gray-900 text-green-400 p-4 rounded-md overflow-x-auto">
          <pre className="text-sm">
{`// Importar os componentes
import { LogoLoading, LoadingScreen, AnimatedLogo } from '@/components/common';

// LogoLoading simples
<LogoLoading size={40} />

// AnimatedLogo com animação específica
<AnimatedLogo size={80} animationType="rotate" />

// LoadingScreen completo
const [isLoading, setIsLoading] = useState(false);

if (isLoading) {
  return <LoadingScreen message="Carregando..." />;
}

// Em um botão
<button disabled={isLoading}>
  {isLoading ? (
    <LogoLoading size={20} />
  ) : (
    'Salvar'
  )}
</button>`}
          </pre>
        </div>
      </section>
    </div>
  );
};
